import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import PublicSEO from '../../components/public/PublicSEO';
import { getBrancaSlug } from '../../utils/slug';
import { publicPath } from '../../utils/canonicalUrl';
import { getSnapApiData } from '../../utils/snapStore';
import { buildBreadcrumb, buildCollectionPage, SITE_URL } from '../../utils/jsonLd';
import './DisciplinePage.css';

const DisciplinePage = () => {
  const snapBranche = getSnapApiData('/public/branche');
  const initialBranche = Array.isArray(snapBranche) ? snapBranche : [];
  const [branche, setBranche] = useState(initialBranche);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        let res;
        try {
          res = await api.get('/public/branche');
        } catch {
          await new Promise((r) => setTimeout(r, 400));
          if (cancelled) return;
          res = await api.get('/public/branche');
        }
        if (cancelled) return;
        setBranche(Array.isArray(res.data) ? res.data : []);
        setError(null);
      } catch (err) {
        console.error('Errore caricamento discipline:', err);
        if (!cancelled) {
          setBranche((prev) => {
            if (prev.length === 0) {
              setError(err.response?.data?.message || 'Impossibile caricare le discipline.');
            }
            return prev;
          });
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="discipline-page">
        <PublicSEO title="Discipline mediche" canonicalPath="/discipline" />
        <div className="container">
          <p className="discipline-page-error">{error}</p>
        </div>
      </div>
    );
  }

  const jsonLd = [
    buildCollectionPage({
      canonicalPath: '/discipline',
      name: 'Discipline mediche',
      description: 'Elenco delle discipline e specializzazioni mediche disponibili per visite online su Virtual Clinic.',
    }),
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Discipline' },
    ]),
    ...(branche.length > 0 ? [{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      numberOfItems: branche.length,
      itemListElement: branche.map((b, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `${SITE_URL}/discipline/${getBrancaSlug(b)}`,
        name: (b.branca || '').replace(/^./, (c) => c.toUpperCase()),
      })),
    }] : []),
  ];

  return (
    <div className="discipline-page">
      <PublicSEO
        title="Discipline mediche - Specializzazioni | Virtual Clinic"
        description="Scopri le discipline e le specializzazioni mediche disponibili per visite online. Scegli la disciplina e trova il medico adatto a te."
        canonicalPath="/discipline"
        keywords="discipline mediche, specializzazioni mediche, branche mediche, telemedicina specialistica"
        ogImageAlt="Discipline e specializzazioni mediche su Virtual Clinic"
        jsonLd={jsonLd}
      />
      <div className="container discipline-page-container">
        <header className="discipline-page-header">
          <h1 className="discipline-page-title">Discipline mediche</h1>
          <p className="discipline-page-intro">
            Scegli la disciplina o specializzazione che ti interessa e consulta i medici disponibili per visite online e videoconsulti.
          </p>
        </header>

        <div className="discipline-grid" role="list">
          {branche.map((b) => {
            const nome = (b.branca || '').replace(/^./, c => c.toUpperCase());
            const slug = getBrancaSlug(b);
            return (
              <div key={b.id} className="discipline-card" role="listitem">
                <Link to={publicPath(`/discipline/${slug}`)} className="discipline-card-link">
                  <span className="discipline-card-name">{nome}</span>
                  {b.intro && <span className="discipline-card-intro">{b.intro}</span>}
                </Link>
              </div>
            );
          })}
        </div>

        {branche.length === 0 && (
          <p className="discipline-page-empty">Nessuna disciplina disponibile al momento.</p>
        )}
      </div>
    </div>
  );
};

export default DisciplinePage;
